// İSG uzmanı portalı — İzin detayı: Kontrol checklist (api-contract §25.3). İznin permit tipindeki
// kontrol itemları (`controlItems`; tipin kontrol itemları LEFT JOIN iznin işaretlemesi) her biri için
// **işaretle/kaldır** toggle (+ opsiyonel not) → `POST /permits/{uuid}/control-checks
// { controlItemUuid, isChecked, note? }` (upsert; idempotent). İşaretlemede/notta başarı sonrası izin
// detayı invalidate (checkedByName/checkedAt/isChecked yeniden çözülür). İşaretlenmişse işaretleyen İSG
// uzmanı + tarih rozeti. **İç checklist** — supplier GÖRMEZ (D4). Veri üst `GET /permits/{uuid}`'ten.
//
// **Durum kapısı (api-contract §25.3; brief 102).** Tik (işaretle/kaldır) + not kaydetme yalnız
// izin `Active` iken yapılabilir (kontrol listesi iş başladıktan sonra — giriş belgeleri oluştu —
// sahada doldurulur). `Active` değilken tik + not kontrolleri **devre dışı** + ipucu gösterilir;
// backend aksi durumda `409 PERMIT_INVALID_STATE` döner (edge → kullanıcı-dostu mesaj). **Madde
// ekle/sil her aşamada serbesttir** (İSG şablonu düzenler) — bu kapıdan **etkilenmez** (disabled EDİLMEZ).

import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  type PermitStatus,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  Tag,
  type WorkPermitControlItemDto,
  currentCulture,
  permitsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ListChecks, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import { permitsKeys } from '../../lib/permitsKeys';
import { TabPanelIntro } from '../TabPanelIntro';

interface PermitControlChecklistPanelProps {
  permitUuid: string;
  /** İznin kontrol itemları (§25.3; tipe bağlı yoksa `[]`). */
  controlItems: WorkPermitControlItemDto[];
  /**
   * İznin durumu (§14). Tik/not **yalnız `Active`** iken (§25.3; brief 102); aksi durumda
   * kontroller kilitlenir + ipucu. **Madde ekle/sil bu kapıdan etkilenmez** (her aşamada serbest).
   */
  permitStatus: PermitStatus;
}

const addInputClass =
  'flex-1 rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg ' +
  'placeholder:text-muted transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-60';

const noteInputClass =
  'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg ' +
  'placeholder:text-muted transition-colors focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-60';

export function PermitControlChecklistPanel({
  permitUuid,
  controlItems,
  permitStatus,
}: PermitControlChecklistPanelProps) {
  const { t } = useTranslation();

  // Tik/not kapısı (§25.3; brief 102): yalnız izin `Active` iken. Değilse tik + not devre dışı
  // + ipucu. **Madde ekle/sil bundan etkilenmez** (her aşamada serbest).
  const checksLocked = permitStatus !== 'Active';

  return (
    <div className="flex flex-col gap-3">
      <TabPanelIntro>{t('permits.controls.subtitle')}</TabPanelIntro>

      {checksLocked ? <Alert tone="info">{t('permits.controls.lockedHint')}</Alert> : null}

      {/* İSG uzmanı izin snapshot'ına madde ekler (SPEC §2.1; tipten bağımsız). Durum kapısından
          ETKİLENMEZ — her aşamada açık. Yetki backend'de (reviewer); yetkisiz 403 hata olarak yansır. */}
      <ControlItemComposer permitUuid={permitUuid} />

      <Table caption={t('permits.detail.section.controls')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('permits.controls.col.item')}</TableHead>
            <TableHead>{t('permits.controls.note')}</TableHead>
            <TableHead>{t('permits.controls.col.status')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('permits.common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {controlItems.length === 0 ? (
            <TableMessageRow colSpan={4}>
              <EmptyState icon={ListChecks} title={t('permits.controls.empty')} />
            </TableMessageRow>
          ) : (
            controlItems.map((item) => (
              <ControlCheckRow
                key={item.uuid}
                permitUuid={permitUuid}
                item={item}
                checksLocked={checksLocked}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** İzne yeni kontrol maddesi ekleme kutusu (İSG uzmanı; SPEC §2.1). Boş metin gönderilmez. */
function ControlItemComposer({ permitUuid }: { permitUuid: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const trimmed = text.trim();

  const mutation = useMutation({
    mutationFn: () => permitsApi.addPermitControlItem(permitUuid, { text: trimmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setText('');
    },
  });

  return (
    <form
      // Kutulu (`border-dashed`) şerit KALKAR — `PermitProtectiveEquipmentPanel.tsx`'in AYNI
      // notu; Tile'a bitişik bir üst şerit + ghost (dolu DEĞİL) Ekle düğmesi.
      className="flex flex-col gap-2 border-b border-border px-1 py-2 carbon:px-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed.length > 0) {
          mutation.mutate();
        }
      }}
    >
      <div className="flex items-center gap-2">
        <input
          type="text"
          className={addInputClass}
          placeholder={t('permits.controls.addPlaceholder')}
          aria-label={t('permits.controls.add')}
          value={text}
          disabled={mutation.isPending}
          onChange={(e) => setText(e.target.value)}
        />
        <Button
          type="submit"
          variant="ghost"
          size="sm"
          loading={mutation.isPending}
          disabled={trimmed.length === 0 || mutation.isPending}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('permits.controls.add')}
        </Button>
      </div>
      {mutation.isError ? (
        <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
      ) : null}
    </form>
  );
}

/**
 * Tek kontrol itemı satırı — işaretle/kaldır toggle + opsiyonel not. Toggle son kaydedilmiş notu
 * korur (`item.note`); not editörü kendi "Kaydet" aksiyonuyla mevcut işaret durumunu (`item.isChecked`)
 * koruyarak notu yazar. İkisi de aynı upsert ucunu çağırır ve izin detayını invalidate eder.
 */
function ControlCheckRow({
  permitUuid,
  item,
  checksLocked,
}: {
  permitUuid: string;
  item: WorkPermitControlItemDto;
  /** İzin `Active` değil → tik + not kaydet devre dışı (§25.3). Silme bundan etkilenmez. */
  checksLocked: boolean;
}) {
  const { t } = useTranslation();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const [note, setNote] = useState(item.note ?? '');
  const [removeOpen, setRemoveOpen] = useState(false);
  const noteDirty = note.trim() !== (item.note ?? '').trim();

  const mutation = useMutation({
    mutationFn: (body: { isChecked: boolean; note: string | null }) =>
      permitsApi.setControlCheck(permitUuid, {
        controlItemUuid: item.uuid,
        isChecked: body.isChecked,
        note: body.note,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
    },
  });

  // İSG uzmanı maddeyi snapshot'tan siler (SPEC §2.1). Onay diyaloğu ile; başarıda detay invalidate.
  const removeMutation = useMutation({
    mutationFn: () => permitsApi.removePermitControlItem(permitUuid, item.uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setRemoveOpen(false);
    },
  });

  /** Boş/whitespace notu `null`'a indirger (opsiyonel — backend'e `null` gider). */
  function nullableNote(value: string): string | null {
    return value.trim().length > 0 ? value.trim() : null;
  }

  return (
    <TableRow>
      <TableCell>
        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-primary disabled:cursor-not-allowed disabled:opacity-60"
            checked={item.isChecked}
            disabled={checksLocked || mutation.isPending}
            onChange={() =>
              // Toggle: son kaydedilmiş notu koru (§25.3 upsert). Yalnız izin `Active` iken (kapı).
              mutation.mutate({ isChecked: !item.isChecked, note: item.note })
            }
          />
          <span className="text-sm font-medium text-fg">{item.text}</span>
        </label>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <input
            type="text"
            className={noteInputClass}
            placeholder={t('permits.controls.notePlaceholder')}
            value={note}
            disabled={checksLocked || mutation.isPending}
            aria-label={t('permits.controls.note')}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={checksLocked || !noteDirty || mutation.isPending}
            onClick={() => mutation.mutate({ isChecked: item.isChecked, note: nullableNote(note) })}
          >
            {t('permits.controls.noteSave')}
          </Button>
        </div>
        {mutation.isError ? (
          <p className="mt-1 text-xs text-danger">{resolveApiErrorMessage(mutation.error, t)}</p>
        ) : null}
      </TableCell>
      <TableCell>
        {item.isChecked ? (
          <Tag color="green">
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            {item.checkedByName
              ? t('permits.controls.checkedBy', {
                  name: item.checkedByName,
                  date: formatDateTime(item.checkedAt, culture),
                })
              : t('permits.controls.checked')}
          </Tag>
        ) : (
          <Tag color="gray">{t('permits.controls.unchecked')}</Tag>
        )}
      </TableCell>
      <TableCell className="w-12 px-2">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('permits.controls.remove')}
          disabled={mutation.isPending || removeMutation.isPending}
          onClick={() => setRemoveOpen(true)}
        >
          <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
        </Button>
        <ConfirmDialog
          open={removeOpen}
          onOpenChange={(open) => !open && setRemoveOpen(false)}
          title={t('permits.controls.remove.title')}
          body={t('permits.controls.remove.body', { text: item.text })}
          confirmLabel={t('permits.controls.remove.confirm')}
          confirmVariant="danger"
          loading={removeMutation.isPending}
          error={removeMutation.isError ? removeMutation.error : undefined}
          onConfirm={() => removeMutation.mutate()}
        />
      </TableCell>
    </TableRow>
  );
}
